import api from "@/app/api";
import Guid from "@/app/types/common/guid";
import { Instant, LocalDate, LocalTime } from "@js-joda/core";
import {ModelObject, BaseModelObject, ValidationRule} from "@/app/services/modelObject";
import ServiceBase from "@/app/services/serviceBase";
import converters from "@/app/services/converters"
import { EnumAccountsPackageType } from "./enums/enumAccountsPackageType.generated";

// Model Objects

export class AccountLinkSettings extends BaseModelObject<AccountLinkSettings> {

    /**
    * Determines whether purchase orders should be posted on connected accounts links.
    */
    public postPurchaseOrders: boolean = false;
    /**
    * If true, orders and credits are posted to the accounts package in an approved state. Applicable only to some directly-linked packages (eg Xero).
    */
    public postInvoicesAsApproved: boolean = false;
    /**
    * If true, when an order is completed it will automatically be set to posting.
    */
    public postOrdersOnCompletion: boolean = false;
    private _defaultPurchaseGlCodeId: Guid | null = null;
    /**
    * Default purchase gl code id. If provided then products with no purchase gl code will be posted with the provided gl code id.On Sage Online, the priority for purchase gl code is 1. BM item 2. Sage Online item (if exist) 3. DefaultPurchaseGlCodeId (this field) 4. 1000 (hard-coded gl code)
    */
    public get defaultPurchaseGlCodeId(): Guid | null { return this._defaultPurchaseGlCodeId }
    public set defaultPurchaseGlCodeId(value: Guid | null) {
      this._defaultPurchaseGlCodeId = value === undefined ? null : value;
    }

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.postPurchaseOrders = jsonSerializedObject.postPurchaseOrders;
          this.postInvoicesAsApproved = jsonSerializedObject.postInvoicesAsApproved;
          this.postOrdersOnCompletion = jsonSerializedObject.postOrdersOnCompletion;
          this.defaultPurchaseGlCodeId = jsonSerializedObject.defaultPurchaseGlCodeId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.defaultPurchaseGlCodeId);
        }

        this.setDirtyCheckpoint();
    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.postPurchaseOrders = this.postPurchaseOrders;
        toRet.postInvoicesAsApproved = this.postInvoicesAsApproved;
        toRet.postOrdersOnCompletion = this.postOrdersOnCompletion;
        toRet.defaultPurchaseGlCodeId = this.defaultPurchaseGlCodeId == null ? null : this.defaultPurchaseGlCodeId.toString();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "defaultPurchaseGlCodeId", rule: (v: any) => v == null || !v.isEmpty() || "AccountLinkSettings.DefaultPurchaseGlCodeId cannot be empty"},
    ];
}


export class AccountsLinkDetails extends ModelObject<AccountsLinkDetails> {

    public tenantId: Guid = Guid.createEmpty();
    /**
    * The type of the current accounts provider : 0 = None, 1 = Xero, 2 = QuickBooksLive, 3 = QuickBooksSandbox, 4 = SageOne, 5 = Sage50, 6 = Sage200, 7 = BrewmanApi, 8 = CustomCsv, 9 = GreenTree, 10 = Sage200Web, 11 = Lexoffice, 12 = Fortnox, 13 = SageOneZa
    */
    public accountsPackageType: EnumAccountsPackageType = EnumAccountsPackageType.none;
    /**
    * Identifies if the link is ready for communicating. (Ie established and thought still connected.
    */
    public ready: boolean = false;
    public settings: AccountLinkSettings = new AccountLinkSettings();

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.tenantId = Guid.parseOrEmpty(jsonSerializedObject.tenantId);
          this.accountsPackageType = EnumAccountsPackageType.getByValue(jsonSerializedObject.accountsPackageType, this.accountsPackageType);
          this.ready = jsonSerializedObject.ready;
          this.settings = new AccountLinkSettings(jsonSerializedObject.settings);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.tenantId = this.tenantId.toString();
        toRet.accountsPackageType = this.accountsPackageType.value;
        toRet.ready = this.ready;
        toRet.settings = this.settings.toJsonSerializedObject();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "tenantId", rule: (v: any) => !v.isEmpty() || "AccountsLinkDetails.TenantId cannot be empty"},
            {field: "settings", rule: (v: any) => !!v || "settings is required"},
            {field: "settings", rule: (v: any) => v.validate()},
    ];
}


export class CompleteSignUpWithXeroResult extends ModelObject<CompleteSignUpWithXeroResult> {

    public firstName: string = "";
    public lastName: string = "";
    public fullName: string = "";
    public emailAddress: string = "";

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.firstName = jsonSerializedObject.firstName;
          this.lastName = jsonSerializedObject.lastName;
          this.fullName = jsonSerializedObject.fullName;
          this.emailAddress = jsonSerializedObject.emailAddress;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.firstName = this.firstName;
        toRet.lastName = this.lastName;
        toRet.fullName = this.fullName;
        toRet.emailAddress = this.emailAddress;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
    ];
}


export class SageOneZaCompany extends ModelObject<SageOneZaCompany> {

    public id: string = "";
    public name: string = "";

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.id = jsonSerializedObject.id;
          this.name = jsonSerializedObject.name;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.id = this.id;
        toRet.name = this.name;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
    ];
}


export class AccountsGlCode extends ModelObject<AccountsGlCode> {

    public code: string = "";
    public name: string = "";

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.code = jsonSerializedObject.code;
          this.name = jsonSerializedObject.name;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.code = this.code;
        toRet.name = this.name;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
    ];
}


export class AccountsTaxCode extends ModelObject<AccountsTaxCode> {

    public code: string = "";
    public name: string = "";
    public percentage: number = 0;
    public appliesToSales: boolean = false;
    public appliesToPurchases: boolean = false;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.code = jsonSerializedObject.code;
          this.name = jsonSerializedObject.name;
          this.percentage = jsonSerializedObject.percentage;
          this.appliesToSales = jsonSerializedObject.appliesToSales;
          this.appliesToPurchases = jsonSerializedObject.appliesToPurchases;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.code = this.code;
        toRet.name = this.name;
        toRet.percentage = this.percentage;
        toRet.appliesToSales = this.appliesToSales;
        toRet.appliesToPurchases = this.appliesToPurchases;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
    ];
}


export class AccountsLinkService extends ServiceBase {

  public async getAccountsLinkDetails(tenantId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<AccountsLinkDetails> {
    const req = {
    tenantId:tenantId.toString(),
    };
    const extractor = (response:any) => new AccountsLinkDetails(response.accountsLinkDetails);;
    return this.makeJsonRequest("accountsLink/v1/getAccountsLinkDetails", req, false, extractor, opts);
  };

  public async getAccountsLinkLogonUrl(tenantId: Guid, accountsPackageType: EnumAccountsPackageType, callbackUri: string, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<string> {
    const req = {
    tenantId:tenantId.toString(),
    accountsPackageType:accountsPackageType.value,
    callbackUri:callbackUri,
    };
    const extractor = (response:any) => response.accountsLinkLogonUrl;;
    return this.makeJsonRequest("accountsLink/v1/getAccountsLinkLogonUrl", req, false, extractor, opts);
  };

  public async disconnectAccountsLink(tenantId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:tenantId.toString(),
    };
    const extractor = null;
    return this.makeJsonRequest("accountsLink/v1/disconnectAccountsLink", req, true, extractor, opts);
  };

  public async setExternalAccountsLink(tenantId: Guid, accountsPackageType: EnumAccountsPackageType, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:tenantId.toString(),
    accountsPackageType:accountsPackageType.value,
    };
    const extractor = null;
    return this.makeJsonRequest("accountsLink/v1/setExternalAccountsLink", req, true, extractor, opts);
  };

  public async performXeroAccountsLink(tenantId: Guid, temporaryCode: string, state: string, callbackUri: string, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:tenantId.toString(),
    temporaryCode:temporaryCode,
    state:state,
    callbackUri:callbackUri,
    };
    const extractor = null;
    return this.makeJsonRequest("accountsLink/v1/performXeroAccountsLink", req, true, extractor, opts);
  };

  public async performFortnoxAccountsLink(tenantId: Guid, authorizationCode: string, state: string, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:tenantId.toString(),
    authorizationCode:authorizationCode,
    state:state,
    };
    const extractor = null;
    return this.makeJsonRequest("accountsLink/v1/performFortnoxAccountsLink", req, true, extractor, opts);
  };

  public async completeQuickBooksAccountsLink(tenantId: Guid, callbackUri: string, authorizationCode: string, state: string, realmId: string, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:tenantId.toString(),
    callbackUri:callbackUri,
    authorizationCode:authorizationCode,
    state:state,
    realmId:realmId,
    };
    const extractor = null;
    return this.makeJsonRequest("accountsLink/v1/completeQuickBooksAccountsLink", req, true, extractor, opts);
  };

  public async completeSageOneAccountsLink(tenantId: Guid, callbackUri: string, authorizationCode: string, state: string, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:tenantId.toString(),
    callbackUri:callbackUri,
    authorizationCode:authorizationCode,
    state:state,
    };
    const extractor = null;
    return this.makeJsonRequest("accountsLink/v1/completeSageOneAccountsLink", req, true, extractor, opts);
  };

  public async performLexofficeAccountsLink(tenantId: Guid, apiKey: string, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:tenantId.toString(),
    apiKey:apiKey,
    };
    const extractor = null;
    return this.makeJsonRequest("accountsLink/v1/performLexofficeAccountsLink", req, true, extractor, opts);
  };

  public async saveSettings(tenantId: Guid, settings: AccountLinkSettings, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:tenantId.toString(),
    settings:settings.toJsonSerializedObject(),
    };
    const extractor = null;
    return this.makeJsonRequest("accountsLink/v1/saveSettings", req, true, extractor, opts);
  };

  public async getSageOneZaCompanies(sageUsername: string, sagePassword: string, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<SageOneZaCompany[]> {
    const req = {
    sageUsername:sageUsername,
    sagePassword:sagePassword,
    };
    const extractor = (response:any) => response.companies.map((v: any) => new SageOneZaCompany(v));;
    return this.makeJsonRequest("accountsLink/v1/getSageOneZaCompanies", req, false, extractor, opts);
  };

  public async completeSageOneZaLink(tenantId: Guid, sageUsername: string, sagePassword: string, sageCompanyId: string, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:tenantId.toString(),
    sageUsername:sageUsername,
    sagePassword:sagePassword,
    sageCompanyId:sageCompanyId,
    };
    const extractor = null;
    return this.makeJsonRequest("accountsLink/v1/completeSageOneZaLink", req, true, extractor, opts);
  };

  public async getAvailableAccountsGlCodes(tenantId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<AccountsGlCode[] | null> {
    const req = {
    tenantId:tenantId.toString(),
    };
    const extractor = (response:any) => response.accountsGlCodes == null ? null : response.accountsGlCodes.map((v: any) => new AccountsGlCode(v));;
    return this.makeJsonRequest("accountsLink/v1/getAvailableAccountsGlCodes", req, false, extractor, opts);
  };

  public async getAvailableAccountsTaxCodes(tenantId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<AccountsTaxCode[] | null> {
    const req = {
    tenantId:tenantId.toString(),
    };
    const extractor = (response:any) => response.accountsTaxCodes == null ? null : response.accountsTaxCodes.map((v: any) => new AccountsTaxCode(v));;
    return this.makeJsonRequest("accountsLink/v1/getAvailableAccountsTaxCodes", req, false, extractor, opts);
  };
}

export const accountsLinkService = new AccountsLinkService();